import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, Button, Typography, CircularProgress, useTheme, LinearProgress, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { motion, AnimatePresence } from "framer-motion";
import { TestAttempt, SkillName } from '../UserAndProfile/UserContext';
import { TestQuestion, UserAnswerRecord } from '../../types';
import { GradeDiagnosticTest } from './GradeTest';
import { PromptContext } from '../PromptContext';
import ChatComponent from '../ChatComponent';

// Demo-specific interfaces
interface DemoTestState {
  isActive: boolean;
  currentQuestionIndex: number;
  answers: UserAnswerRecord[];
  markedQuestions: number[];
  totalQuestions: number;
  startTime: number;
}

interface DemoDiagnosticTestProps {
  onTestComplete: (results: TestAttempt) => void;
  onExit: () => void;
}

// Timer component for demo (simplified version)
interface DemoTimerRef {
  getElapsedTime: () => number;
}

const DemoTimer: React.ForwardRefRenderFunction<DemoTimerRef, {}> = (props, ref) => {
  const [startTime] = useState(Date.now());
  
  React.useImperativeHandle(ref, () => ({
    getElapsedTime: () => Date.now() - startTime
  }));

  return null; // Hidden timer
};

const DemoTimerComponent = React.forwardRef(DemoTimer);

const DemoDiagnosticTest: React.FC<DemoDiagnosticTestProps> = ({ onTestComplete, onExit }) => {
  const theme = useTheme();
  
  // Access PromptContext for proper rendering and chat functionality
  const {
    renderLatexOrText,
    setQuestionText,
    setChoices,
    setCorrectAnswer,
    setExplanationText,
    setSelectedSkill,
    clearChat
  } = useContext(PromptContext)!;
  
  // Demo test state (local, not using PromptContext)
  const [testState, setTestState] = useState<DemoTestState>({
    isActive: false,
    currentQuestionIndex: 0,
    answers: [],
    markedQuestions: [],
    totalQuestions: 0,
    startTime: 0
  });

  // Test data and UI state
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Current question state
  const [currentQuestion, setCurrentQuestion] = useState<TestQuestion | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [lastQuestionTimestamp, setLastQuestionTimestamp] = useState<number>(0);

  // Timer reference
  const timerRef = useRef<DemoTimerRef>(null);

  // Load demo diagnostic test questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch('/satquestions/demo-diagnostic-test.json');
        if (!response.ok) {
          throw new Error('Failed to load demo diagnostic test questions');
        }
        
        const data = await response.json();
        setQuestions(data.questions);
        setLoading(false);
        
        // Start the test immediately instead of showing intro
        if (data.questions.length > 0) {
          clearChat();
          setTestState({
            isActive: true,
            currentQuestionIndex: 0,
            answers: [],
            markedQuestions: [],
            totalQuestions: data.questions.length,
            startTime: Date.now()
          });
          setLastQuestionTimestamp(0);
        }
      } catch (err) {
        setError('Error loading demo test: ' + (err instanceof Error ? err.message : String(err)));
        setLoading(false);
      }
    };
    
    fetchQuestions();
  }, []); // Empty dependency array - only run once on mount
  
  // Set current question when index changes and update PromptContext
  useEffect(() => {
    if (questions.length > 0 && testState.isActive) {
      const question = questions[testState.currentQuestionIndex];
      setCurrentQuestion(question);

      // Reset previous question state
      setSelectedAnswer('');
      
      // Update PromptContext for ChatComponent
      setQuestionText(question.question);
      setChoices(question.choices);
      setCorrectAnswer(question.answer);
      setExplanationText(question.explanation);
      setSelectedSkill(question.skill);
    }
  }, [testState.currentQuestionIndex, questions, testState.isActive, setQuestionText, setChoices, setCorrectAnswer, setExplanationText, setSelectedSkill]);

  // Start the demo test
  const handleStartTest = () => {
    if (questions.length > 0) {
      setTestState({
        isActive: true,
        currentQuestionIndex: 0,
        answers: [],
        markedQuestions: [],
        totalQuestions: questions.length,
        startTime: Date.now()
      });
      setLastQuestionTimestamp(0);
    } else {
      console.error('No questions found for demo test.');
    }
  };
  
  // Handle answer selection
  const handleAnswerSelect = (answer: string) => {
    console.log('Answer selected:', answer); // Debug log
    setSelectedAnswer(answer);
  };
  
  // Handle navigation to next question
  const handleNextQuestion = () => {
    let timeSpentOnQuestion = 0;
    const currentTime = timerRef.current?.getElapsedTime() || 0;

    timeSpentOnQuestion = currentTime - lastQuestionTimestamp;

    // If an answer was selected, record it
    if (selectedAnswer && currentQuestion) {
      const selectedLetter = selectedAnswer[0];
      const correctLetter = currentQuestion.answer[0];
      const isCorrect = selectedLetter === correctLetter;
      const newAnswer: UserAnswerRecord = {
        questionIndex: testState.currentQuestionIndex,
        selectedAnswer: selectedAnswer,
        correctAnswer: correctLetter,
        isCorrect: isCorrect,
        skill: currentQuestion.skill,
        difficulty: currentQuestion.difficulty,
        timeSpent: timeSpentOnQuestion
      };

      const newAnswers = [...testState.answers, newAnswer];
      setLastQuestionTimestamp(currentTime);

      if (testState.currentQuestionIndex < questions.length - 1) {
        // Update both answers and currentQuestionIndex in one state update
        setTestState(prev => ({
          ...prev,
          answers: newAnswers,
          currentQuestionIndex: prev.currentQuestionIndex + 1
        }));
      } else {
        // This is the last question, finish the test
        handleFinishTest(newAnswers);
      }
    } else {
      // No answer selected, just move to next question
      if (testState.currentQuestionIndex < questions.length - 1) {
        setTestState(prev => ({
          ...prev,
          currentQuestionIndex: prev.currentQuestionIndex + 1
        }));
      } else {
        // This is the last question, finish the test
        handleFinishTest(testState.answers);
      }
    }
  };
  
  // Handle navigation to previous question
  const handlePreviousQuestion = () => {
    if (testState.currentQuestionIndex > 0) {
      setTestState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1
      }));
    }
  };
  
  // Toggle marking a question for review
  const toggleMarkQuestion = () => {
    setTestState(prev => ({
      ...prev,
      markedQuestions: prev.markedQuestions.includes(prev.currentQuestionIndex)
        ? prev.markedQuestions.filter(q => q !== prev.currentQuestionIndex)
        : [...prev.markedQuestions, prev.currentQuestionIndex]
    }));
  };
  
  // Finish the demo test and pass results to parent
  const handleFinishTest = (finalAnswers: UserAnswerRecord[]) => {
    if (questions.length > 0) {
      const totalElapsedTime = timerRef.current?.getElapsedTime() || 0;

      // Create test attempt record for demo
      let testAttempt: TestAttempt = {
        id: `demo-diagnostic-${Date.now()}`,
        testType: 'diagnostic',
        date: new Date().toISOString(),
        completed: true,
        sections: [
          {
            sectionNumber: 1, 
            questions: finalAnswers,
            elapsedTime: totalElapsedTime
          }
        ]
      };

      // Calculate scores using the utility function
      const gradedScores = GradeDiagnosticTest(testAttempt);

      // Update the testAttempt with the calculated scores
      testAttempt.readingWritingScore = gradedScores.readingWritingScore;
      testAttempt.mathScore = gradedScores.mathScore;
      testAttempt.totalScore = gradedScores.totalScore;
      testAttempt.diagnosticReadingWritingScores = gradedScores.diagnosticReadingWritingScores;
      testAttempt.diagnosticMathScores = gradedScores.diagnosticMathScores;
      testAttempt.diagnosticTotalScores = gradedScores.diagnosticTotalScores;

      console.log('Demo test completed:', testAttempt);

      // Pass results to parent component
      onTestComplete(testAttempt);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading demo test...</Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: {xs: 1, md: 3}, textAlign: 'center' }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    );
  }
  
  // Render active demo test (no pre-test intro screen)
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <DemoTimerComponent ref={timerRef} />
      
      {/* Demo test header */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ color: 'white' }}>
          Demo Diagnostic Test
        </Typography>
        <Typography variant="body1" sx = {{ color: 'white'}}>
          Question {testState.currentQuestionIndex + 1} of {questions.length}
        </Typography>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ width: '100%', p: 2, bgcolor: 'background.paper' }}>
        <LinearProgress 
          variant="determinate" 
          value={((testState.currentQuestionIndex + 1) / questions.length) * 100}
          sx={{
            height: 10, 
            borderRadius: 5, 
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 5, 
              backgroundColor: 'custom.green',
            },
          }}
        />
        <Box sx={{
          mt: 1, 
          display: 'flex', 
          justifyContent: 'space-between', 
          px: 1, 
          fontSize: '0.875rem', 
          color: 'text.secondary',
        }}>
          <span>0</span>
          <span>1</span>
          <span>2</span>
          <span>3</span>
          <span>4</span>
        </Box>
      </Box>
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0, p: {xs: 1, md: 3} }}>
        {/* Current question */}
        <AnimatePresence mode="wait">
          {currentQuestion && (
            <motion.div
              key={testState.currentQuestionIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{
                flex: 1, 
                overflow: 'auto', 
                bgcolor: 'background.paper', 
                px: {xs: 1, md: 3}, 
                py: {xs: 2, md: 3}, 
                borderRadius: 2, 
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                borderColor: 'divider',
                mb: 2
              }}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, position: 'relative'}}>
                  {/* Top row with question number */}
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                      Question {testState.currentQuestionIndex + 1} of {questions.length}
                    </Typography>
                  </Box>
                  
                  {/* Skill and difficulty badges - absolutely positioned */}
                  <Box sx={{position: 'absolute', top: -8, right: 0, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end', zIndex: 1}}>
                    <Chip label={currentQuestion.skill} color="primary" variant="outlined" size="small"/>
                    <Chip label={currentQuestion.difficulty} color={currentQuestion.difficulty === 'Easy' ? 'success' : currentQuestion.difficulty === 'Medium' ? 'warning' : 'error'} variant="outlined" size="small"/>
                  </Box>
                  
                  {/* Main content area with question and answers side by side */}
                  <Box sx={{display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: {xs: 0, md: 3}, mt: 2}}>
                    {/* Question text on the left */}
                    <Box sx={{flex: '3 1 0', minWidth: '300px'}}>
                      {currentQuestion.pictureURL && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          <img 
                            src={currentQuestion.pictureURL}
                            alt={`Diagram for question ${testState.currentQuestionIndex + 1}`}
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '300px',
                              height: 'auto', 
                              display: 'block', 
                              margin: '0 auto', 
                              borderRadius: '4px', 
                              border: `1px solid ${theme.palette.divider}` 
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              console.warn(`Failed to load image: ${currentQuestion.pictureURL}`);
                            }}
                          />
                        </Box>
                      )}
                      {renderLatexOrText(currentQuestion.question, 'normal')}
                    </Box>
                    
                    {/* Answer choices on the right */}
                    <Box sx={{flex: '2 1 0', minWidth: '250px'}}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Select your answer:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {currentQuestion.choices.map((choice, index) => {
                          const choiceLetter = String.fromCharCode(65 + index);
                          const isSelected = selectedAnswer === choiceLetter;
                          
                          return (
                            <Box 
                              key={index} 
                              onClick={() => handleAnswerSelect(choiceLetter)}
                              sx={{
                                p: 1, 
                                mb: 0, 
                                borderRadius: 2, 
                                border: 2, 
                                minWidth: { xs: '100%', md: '300px', xl: '500px' },
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? theme.palette.custom.veryLightOrange : 'background.paper',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.01)',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                  bgcolor: isSelected
                                    ? 'rgba(241, 101, 34, 0.12)'
                                    : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              <Typography variant="body1" component="div">
                                {renderLatexOrText(choice, 'normal', true)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                
                {/* Navigation buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    sx={{fontSize: {xs: '.9rem', md: '1.2 rem'}}}
                    startIcon={<ArrowBackIcon />}
                    onClick={handlePreviousQuestion}
                    disabled={testState.currentQuestionIndex === 0}
                  >
                    Previous
                  </Button>
                  <Button variant="outlined" sx={{fontSize: {xs: '.9rem', md: '1.2 rem'}}} 
                  color="secondary" onClick={toggleMarkQuestion}>
                    {testState.markedQuestions.includes(testState.currentQuestionIndex) 
                      ? 'Unmark Question' 
                      : 'Mark for Review'}
                  </Button>
                
                  <Button 
                    variant="contained" 
                    sx={{fontSize: {xs: '.9rem', md: '1.2 rem'}}}
                    color="primary" 
                    onClick={handleNextQuestion} 
                    endIcon={<SkipNextIcon />}
                  >
                    {testState.currentQuestionIndex === questions.length - 1 
                      ? 'Finish Demo' 
                      : 'Next Question'}
                  </Button>
                </Box>
              </Box>
              
              {/* Add ChatComponent at the bottom */}
              <Box sx={{ mt: 2 }}>
                <ChatComponent />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default DemoDiagnosticTest;
